<!-- components/search/SuggestList.vue -->
<template>
    <div class="suggest">
        <div v-if="loading" class="row muted">Searching…</div>
        <div v-else-if="!items.length" class="row muted">No suggestions</div>

        <div class="pill-row">
            <v-chip
                v-for="s in items"
                :key="s.kind + ':' + s.term"
                :color="s.kind === 'tag' ? 'primary' : 'secondary'"
                variant="elevated"
                class="pill"
                @click="emit('select', { type: s.kind, term: s.term })"
            >
                <span>
                    <template v-if="s.kind === 'tag'">#{{ s.term }}</template>
                    <template v-else>{{ s.term }}</template>
                    <span class="count">{{ compact(s.count) }}</span>
                </span>

                <v-avatar v-if="s.sample_thumb" size="20" class="ml-2">
                    <v-img :src="s.sample_thumb" alt="" cover></v-img>
                </v-avatar>
            </v-chip>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import SearchService, { TypeaheadItem } from '@/services/SearchService';

const props = defineProps<{ q: string }>();
const emit = defineEmits<{ (e: 'select', p: { type: 'tag' | 'key'; term: string }): void }>();

const items = ref<TypeaheadItem[]>([]);
const loading = ref(false);
let t: ReturnType<typeof setTimeout> | null = null;
let inflight: AbortController | null = null;

function compact(n?: number) {
    if (typeof n !== 'number') return '';
    try {
        return Intl.NumberFormat(undefined, { notation: 'compact' }).format(n);
    } catch {
        return String(n);
    }
}

async function fetchNow(raw: string) {
    const q = raw.trim().toLowerCase();
    if (q.length < 2) {
        items.value = [];
        return;
    }

    inflight?.abort();
    inflight = new AbortController();
    loading.value = true;

    try {
        const resp = await SearchService.typeahead(q, 12, inflight.signal);
        // If the user typed "#dea", visually coerce to tag
        const forceKind: 'tag' | 'key' | null = q.startsWith('#') ? 'tag' : null;
        items.value = (resp.items ?? []).map((it) => ({
            kind: (forceKind ?? it.kind) as 'tag' | 'key',
            term: it.term,
            count: it.count,
            sample_thumb: it.sample_thumb,
            sample_route: it.sample_route,
        }));
    } catch {
        /* ignore abort/errors */
    } finally {
        if (!inflight?.signal.aborted) loading.value = false;
    }
}

watch(
    () => props.q,
    (val) => {
        if (t) clearTimeout(t);
        // immediate clear so UI doesn’t show stale chips while deleting
        if (!val || val.trim().length < 2) items.value = [];
        t = setTimeout(() => fetchNow(val), 150);
    },
    { immediate: true }
);
</script>

<style scoped>
.pill-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.pill {
    color: #fff;
}
.count {
    margin-left: 6px;
    opacity: 0.75;
}
.row.muted {
    color: #aaa;
    padding: 6px 0;
}
</style>
